import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from './ui/Icon';
import PastelButton from './ui/PastelButton';

const AddMemoryCard = ({ onSaveMemory }) => {
  const [memoryImage, setMemoryImage] = useState(null);
  const [memoryText, setMemoryText] = useState('');
  const [memoryDate, setMemoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setMemoryImage(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (memoryImage && memoryText.trim()) {
      setIsUploading(true);
      const memory = {
        id: Date.now(),
        image: memoryImage,
        text: memoryText,
        date: memoryDate,
        timestamp: new Date().toISOString()
      };
      
      try {
        await onSaveMemory(memory);
        // Reset form
        setMemoryImage(null);
        setMemoryText('');
        setMemoryDate(new Date().toISOString().split('T')[0]);
      } catch (error) {
        console.error('Error saving memory:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const isFormValid = memoryImage && memoryText.trim();

  return (
    <div className="relative bg-white/20 backdrop-blur-md rounded-3xl py-4 px-1.5 sm:py-8 sm:px-5 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 overflow-hidden">
      {/* Inner highlight */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
      
      {/* Image Upload */}
      <div className="relative mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Icon name="camera" size="sm" color="primary" />
          Upload a photo
        </label>
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            id="memory-image-upload"
          />
          <motion.label
            htmlFor="memory-image-upload"
            className="flex items-center justify-center w-full h-40 sm:h-48 border-2 border-dashed border-purple-200/60 rounded-2xl cursor-pointer hover:border-purple-300 transition-all duration-200 bg-white/40 backdrop-blur-sm hover:bg-white/50"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            {memoryImage ? (
              <img
                src={memoryImage}
                alt="Memory preview"
                className="w-full h-full object-cover rounded-2xl"
              />
            ) : (
              <div className="text-center text-purple-500 flex flex-col items-center">
                <motion.div
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Icon name="camera" size="xl" color="primary" />
                </motion.div>
                <p className="text-sm font-medium mt-2">Click to upload image</p>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF up to 10MB</p>
              </div>
            )}
          </motion.label>
        </div>
      </div>

      {/* Text Area */}
      <div className="relative mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Icon name="heart" size="sm" color="memories" />
          Write a memory about this moment...
        </label>
        <textarea
          id="memory-description"
          name="memory-description"
          value={memoryText}
          onChange={(e) => setMemoryText(e.target.value)}
          placeholder="This reminds me of the time when..."
          className="w-full min-h-[120px] p-4 bg-white/50 backdrop-blur-sm border border-white/40 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300/50 focus:border-purple-200/50 transition-all duration-200 resize-none text-sm leading-relaxed"
        />
      </div>

      {/* Date Picker */}
      <div className="relative mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Icon name="star" size="sm" color="bonds" />
          When was this?
        </label>
        <input
          id="memory-date"
          name="memory-date"
          type="date"
          value={memoryDate}
          onChange={(e) => setMemoryDate(e.target.value)}
          className="w-full p-3.5 bg-white/50 backdrop-blur-sm border border-white/40 rounded-2xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300/50 focus:border-purple-200/50 transition-all duration-200 text-sm"
        />
      </div>

      {/* Save Button with micro-interaction */}
      <motion.div
        whileHover={isFormValid && !isUploading ? { y: -2, scale: 1.02 } : {}}
        whileTap={isFormValid && !isUploading ? { scale: 0.98 } : {}}
      >
        <PastelButton
          onClick={handleSave}
          disabled={!isFormValid || isUploading}
          color="lavender"
          className={`w-full py-3.5 px-6 font-semibold text-base ${
            !isFormValid || isUploading
              ? 'opacity-50 cursor-not-allowed'
              : ''
          }`}
        >
          {isUploading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-[3px] border-white/30 border-t-white mr-2"></div>
              Saving Memory...
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Icon name="heart" size="sm" color="#ffffff" />
              Save Memory
            </div>
          )}
        </PastelButton>
      </motion.div>
      
      {/* Helper text */}
      {!isFormValid && (
        <p className="relative text-center text-xs text-gray-500 mt-3">
          {!memoryImage && !memoryText.trim() 
            ? 'Add a photo and description to save'
            : !memoryImage 
              ? 'Add a photo to continue'
              : 'Add a description to continue'}
        </p>
      )}
    </div>
  );
};

export default AddMemoryCard; 